import { useTranslation } from "react-i18next";
import { TableColumn } from "react-data-table-component";
import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
} from "reactstrap";
import { withRouter, useHistory, RouteComponentProps } from "react-router-dom";
import { DebounceInput } from "react-debounce-input";

import { BreadCrumb } from "../../../components/Common";
import {
  ActionButton,
  AppTable,
  ColumnHeader,
  DateColumn,
  TableSpinner,
  TextColumn,
} from "../../../components";
import { ActionTypeEnum } from "../../../enums";
import DeleteModal from "../../../components/Common/DeleteModal";
import { AddEditCurriculumModal } from "./AddEditCurriculumModal";
import { CurriculumDto } from "../../../interfaces/curriculum";
import { PaginateQuery } from "../../../interfaces";
import {
  useGetCurriculaQuery,
  useDeleteCurriculumMutation,
} from "../../../store/api/curriculumApi";
import { useGetEducationSystemSelectListQuery } from "../../../store/api/educationSystemApi";

type TProps = RouteComponentProps & {};

const CurriculaPageComponent: React.FC<TProps> = ({ location }) => {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const langKey = i18n.language === "ar" ? "ar" : "en";

  const readEducationSystemIdFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return (
      params.get("filter[educationSystemId]") ||
      params.get("educationSystemId") ||
      ""
    );
  };

  const [paginateQuery, setPaginateQuery] = useState<PaginateQuery>(() => {
    const educationSystemId = readEducationSystemIdFromUrl();
    return new PaginateQuery(
      10,
      "sequence_number:ASC",
      educationSystemId ? { educationSystemId } : {},
    );
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [deletingItemId, setDeletingItemId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);

  const { data: systems } = useGetEducationSystemSelectListQuery();
  const { data: response, isLoading: loading } =
    useGetCurriculaQuery(paginateQuery);
  const [deleteCurriculum, { isLoading: deleting, error: deleteError }] =
    useDeleteCurriculumMutation();

  document.title = `${t("documentTitle", { pageName: "curricula" })}`;

  const goToEducationalStages = (row: CurriculumDto) => {
    const params = new URLSearchParams();
    if (row.educationSystemId) {
      params.set("educationSystemId", row.educationSystemId);
    }
    params.set("curriculumId", row.id);
    history.push(`/education-system/educational-stages?${params.toString()}`);
  };

  const columns: TableColumn<CurriculumDto>[] = [
    {
      name: <ColumnHeader title="sequenceNumber" />,
      width: "90px",
      cell: (row) => <TextColumn text={`#${row.sequenceNumber ?? "-"}`} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="name" />,
      cell: (row) => (
        <button
          type="button"
          className="btn btn-link p-0 text-primary text-decoration-underline"
          onClick={() => goToEducationalStages(row)}
        >
          {row.name[langKey]}
        </button>
      ),
      sortable: true,
    },
    {
      name: <ColumnHeader title="code" />,
      cell: (row) => <TextColumn text={row.code} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="educationSystem" />,
      cell: (row) =>
        row.educationSystemId ? (
          <button
            type="button"
            className="btn btn-link p-0 text-primary text-decoration-underline"
            onClick={() =>
              history.push(
                `/education-system/curricula?filter[educationSystemId]=${row.educationSystemId}`
              )
            }
          >
            {row.educationSystem?.name?.[langKey] || "-"}
          </button>
        ) : (
          <TextColumn text="-" />
        ),
      sortable: false,
    },
    {
      name: <ColumnHeader title="createdAt" />,
      cell: (row) => <DateColumn date={row.createdAt} />,
      sortable: true,
    },
    {
      name: <ColumnHeader title="action" />,
      sortable: false,
      cell: (row) => (
        <React.Fragment>
          <Button
            color="info"
            outline
            size="sm"
            className="me-1"
            title={t("educationalStages")}
            onClick={() => goToEducationalStages(row)}
          >
            <i className="ri-stack-line me-1"></i>
            {t("educationalStages")}
          </Button>
          <ActionButton
            onAction={() => {
              setEditingItemId(row.id);
              setShowAddEditModal(true);
            }}
            actionType={ActionTypeEnum.EDIT}
          />
          &nbsp;
          <ActionButton
            onAction={() => {
              setDeletingItemId(row.id);
              setShowDeleteModal(true);
            }}
            actionType={ActionTypeEnum.DELETE}
          />
        </React.Fragment>
      ),
    },
  ];

  // Keep filter in sync when arriving via browser back/forward or deep link.
  useEffect(() => {
    const educationSystemId = readEducationSystemIdFromUrl();
    const current = paginateQuery.filter?.educationSystemId || "";
    if (educationSystemId === current) return;
    setPaginateQuery((prev) => ({
      ...prev,
      page: 1,
      filter: {
        ...prev.filter,
        educationSystemId,
      },
    }));
  }, [location.search]);

  useEffect(() => {
    const { limit, page, search, sortBy, filter } = paginateQuery;
    let query = `?page=${page || 1}`;
    if (limit) query += `&limit=${limit}`;
    if (sortBy) query += `&sortBy=${sortBy}`;
    if (search) query += `&search=${search}`;
    if (filter?.educationSystemId)
      query += `&filter[educationSystemId]=${filter.educationSystemId}`;
    if (query !== location.search) {
      history.replace({ search: query });
    }
  }, [paginateQuery]);

  const onDeleteConfirm = async () => {
    try {
      await deleteCurriculum(deletingItemId as string).unwrap();
      setDeletingItemId(null);
      setShowDeleteModal(false);
    } catch (err) {}
  };

  const selectedSystemId = paginateQuery.filter?.educationSystemId || "";
  const selectedSystemName =
    systems?.find((s: any) => s.id === selectedSystemId)?.value?.[langKey] ||
    systems?.find((s: any) => s.id === selectedSystemId)?.value?.en;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={t("curricula")} pageTitle={t("educationSystem")} />
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3 small">
            <button
              type="button"
              className="btn btn-link p-0"
              onClick={() => history.push("/education-system/systems")}
            >
              {t("educationSystems")}
            </button>
            {selectedSystemId && selectedSystemName ? (
              <>
                <span className="text-muted">/</span>
                <span className="fw-medium">{selectedSystemName}</span>
              </>
            ) : null}
          </div>
          <Row>
            <Col xxl={12}>
              <Card>
                <CardBody>
                  <Row className="g-2 mb-3">
                    <Col sm={3} className="mb-2">
                      <Input
                        type="select"
                        value={paginateQuery.filter?.educationSystemId || ""}
                        onChange={(e) =>
                          setPaginateQuery({
                            ...paginateQuery,
                            page: 1,
                            filter: {
                              ...paginateQuery.filter,
                              educationSystemId: e.target.value,
                            },
                          })
                        }
                      >
                        <option value="">{t("allEducationSystems")}</option>
                        {(systems || []).map((s: any) => (
                          <option key={s.id} value={s.id}>
                            {s.value?.[langKey] || s.value?.en}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col sm={4} className="mb-2">
                      <div className="search-box">
                        <DebounceInput
                          className="form-control"
                          placeholder={`${t("searchPlaceholder")}...`}
                          minLength={2}
                          debounceTimeout={300}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) =>
                            setPaginateQuery({
                              ...paginateQuery,
                              search: event.target.value,
                              page: 1,
                            })
                          }
                          value={paginateQuery.search}
                        />
                        <i className="ri-search-line search-icon"></i>
                      </div>
                    </Col>
                    <Col className="col-sm-auto ms-auto">
                      <Button
                        color="success"
                        onClick={() => {
                          setEditingItemId(null);
                          setShowAddEditModal(true);
                        }}
                      >
                        <i className="ri-add-fill me-1 align-bottom"></i>
                        {t("addItem", { item: "curriculumItem" })}
                      </Button>
                    </Col>
                  </Row>
                  <AppTable
                    theme={"dark"}
                    columns={columns}
                    data={response?.data || []}
                    progressPending={loading}
                    progressComponent={<TableSpinner />}
                    pagination
                    paginationServer
                    onChangePage={(page: number) =>
                      setPaginateQuery({ ...paginateQuery, page })
                    }
                    onChangeRowsPerPage={(limit: number, page: number) =>
                      setPaginateQuery({ ...paginateQuery, limit, page })
                    }
                    persistTableHead
                    paginationTotalRows={response?.meta?.totalItems}
                    paginationComponentOptions={{
                      rowsPerPageText: t("rowsPerPage"),
                      rangeSeparatorText: t("of"),
                    }}
                    fixedHeader={true}
                    fixedHeaderScrollHeight="60vh"
                  />
                  <DeleteModal
                    loading={deleting}
                    show={showDeleteModal}
                    onDeleteClick={onDeleteConfirm}
                    onCloseClick={() => setShowDeleteModal(false)}
                    error={
                      deleteError
                        ? (deleteError as any)?.data?.message ||
                          "An error occurred"
                        : undefined
                    }
                  />
                  <AddEditCurriculumModal
                    id={editingItemId}
                    show={showAddEditModal}
                    onCloseClick={() => {
                      setShowAddEditModal(false);
                      setEditingItemId(null);
                    }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export const CurriculaPage = withRouter(CurriculaPageComponent);
